import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { CheckCircle, HourglassEmpty, Storefront } from '@mui/icons-material';
import { Header } from 'components/Header/Header';
import AuthPage from 'components/AuthForm/AuthForm';
import EnrollmentSteps from 'components/EnrollmentSteps/EnrollmentSteps';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import {
  DEFERRED_OPTION,
  PROVIDER_STORAGE_KEY,
  ProviderSelection,
  providerLabel,
} from 'constants/providers';
import { fetchRequestByUser } from 'Helpers/request-by-user';

/**
 * Modulo de configuracion de pagos para abogados que entraron a su subdominio
 * sin haber elegido proveedor durante el onboarding ("configurar despues").
 *
 * Vive en la landing porque es donde ya estan el formulario de KYC y el cliente
 * de la API. Se llega desde el banner del portal (`https://<sub>.dominio/payment-setup`)
 * o directo por URL.
 *
 * Al guardar, la solicitud pasa a tener proveedor y el admin de paymylawyer la ve
 * en el backoffice con `origin: 'subdomain'`, que es la senal que pidio el
 * negocio para distinguir estas altas de las del onboarding.
 */

interface RequestState {
  id: number;
  providerChoice: ProviderSelection | null;
  deferred: boolean;
  paymentReady: boolean;
  usioEnrolled: boolean;
  dwollaEnrolled: boolean;
  status_usio_id: number | null;
  status_dwolla_id: number | null;
  subdomain?: { subdomain?: string };
  info_dwolla?: Record<string, any> | null;
  info_usio?: Record<string, any> | null;
}

const PLATFORM_BASE_URL = process.env.REACT_APP_PLATFORM_BASE_URL || 'paymylawyer.cc';

/**
 * Propósito: Decide si el abogado ya entregó la solicitud de pagos y está
 * esperando la revisión interna. Evita volver a pintar el formulario de KYC
 * (el aviso verde encima del mismo form parecía que el envío no había hecho nada).
 */
const isAwaitingReview = (request: RequestState | null, justSubmitted: boolean): boolean => {
  if (justSubmitted) {
    /**
     * Output:
     * @returns boolean — true en el instante posterior al PUT exitoso, aunque
     *   el request local todavía no se haya recargado
     */
    return true;
  }

  if (!request || request.paymentReady || request.deferred) {
    /**
     * Output:
     * @returns boolean — false si no hay request, ya puede cobrar, o aún no
     *   eligió proveedor (flujo "configurar después")
     */
    return false;
  }

  /**
   * Output:
   * @returns boolean — true cuando ya hay proveedor elegido y la cuenta no
   *   está lista: la solicitud está en cola de revisión
   */
  return Boolean(request.providerChoice && request.providerChoice !== DEFERRED_OPTION);
};

interface StatusViewProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  portalUrl: string | null;
}

/**
 * Propósito: Pantalla de estado (listo o en revisión) con el mismo layout que
 * el resto de `/payment-setup`. Centraliza Header + Paper para no duplicar
 * el markup entre "cuenta lista" y "esperando aprobación".
 */
const PaymentSetupStatusView = ({ icon, title, description, portalUrl }: StatusViewProps) => (
  <div>
    <Header />
    <Container className='container' style={{ paddingTop: '40px', maxWidth: 720 }}>
      <Paper style={{ padding: '48px 32px', textAlign: 'center' }}>
        {icon}
        <Typography variant='h5' gutterBottom style={{ fontWeight: 'bold', marginTop: '12px' }}>
          {title}
        </Typography>
        <Typography
          variant='body1'
          color='textSecondary'
          style={{ maxWidth: 520, margin: '0 auto' }}
        >
          {description}
        </Typography>
        {portalUrl ? (
          <Button
            variant='contained'
            color='primary'
            href={portalUrl}
            startIcon={<Storefront />}
            style={{ marginTop: '24px' }}
          >
            Go to my portal
          </Button>
        ) : null}
      </Paper>
    </Container>
  </div>
);

const PaymentSetupPage = () => {
  const { user } = useLogin();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [request, setRequest] = useState<RequestState | null>(null);
  const [saved, setSaved] = useState(false);

  /**
   * Propósito: Carga la solicitud de pagos del abogado autenticado.
   * "Sin request" se muestra como error en esta página porque /payment-setup
   * solo tiene sentido cuando el onboarding ya creó la solicitud.
   */
  const loadRequest = useCallback(async () => {
    if (!user?.user_id || !user?.token) {
      /**
       * Output:
       * Side effects — Sin sesión no se consulta; la página muestra el login
       */
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const result = await fetchRequestByUser(user);

      if (result.status === 'expired') {
        /**
         * Output:
         * Side effects — El token no sirve; el usuario verá el login de esta página
         */
        setLoadError('Your session expired. Please sign in again.');
        return;
      }

      if (result.status !== 'ok' || !result.request) {
        /**
         * Output:
         * Side effects — Sin request no hay nada que configurar en el portal
         */
        setLoadError(
          result.status === 'error'
            ? result.message
            : 'We could not find a payment application for this account.',
        );
        return;
      }

      const data = result.request as unknown as RequestState;
      setRequest(data);

      /**
       * Si ya tenia una eleccion guardada se muestra esa; si estaba diferido se
       * arranca sin seleccion para obligarlo a elegir.
       */
      /**
       * La elección ya no se pinta aquí: EnrollmentSteps filtra proveedores
       * apagados y abre el mismo KYC que `/enroll`.
       */
    } catch (error: any) {
      /**
       * Output:
       * Side effects — Error de red; se muestra el alert con Retry
       */
      setLoadError(error?.message || 'We could not load your account.');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, user?.token]);

  useEffect(() => {
    if (user?.token) loadRequest();
  }, [user?.token, loadRequest]);

  /**
   * Propósito: Cierra el formulario de KYC del portal tras un envío exitoso.
   * Lee la elección persistida por EnrollmentSteps para el copy de "en revisión".
   */
  const handleEnrollmentSubmitted = () => {
    let storedChoice: ProviderSelection | null = null;
    try {
      storedChoice = localStorage.getItem(PROVIDER_STORAGE_KEY) as ProviderSelection | null;
    } catch (_error) {
      storedChoice = null;
    }

    setRequest((previous) =>
      previous
        ? {
            ...previous,
            deferred: false,
            providerChoice: storedChoice && storedChoice !== DEFERRED_OPTION
              ? storedChoice
              : previous.providerChoice,
          }
        : previous,
    );
    /**
     * Output:
     * Side effects — Pinta la vista de "en revisión" sin recargar
     */
    setSaved(true);
  };

  const subdomain = request?.subdomain?.subdomain;
  const portalUrl = subdomain ? `https://${subdomain}.${PLATFORM_BASE_URL}/login` : null;

  // ---------------------------------------------------------------- sin sesion
  if (!user?.token) {
    return (
      <div>
        <Header />
        <Container className='container' style={{ paddingTop: '40px', maxWidth: 640 }}>
          <Paper style={{ padding: '32px' }}>
            <Typography variant='h5' gutterBottom style={{ fontWeight: 'bold' }}>
              Set up your payment account
            </Typography>
            {/*
              `color='textSecondary'` es la clave de MUI v4: en v5 MUI no la
              reconoce, avisa por consola y el texto se queda con el color
              heredado, asi que el token de "texto atenuado" nunca se aplicaba.
              La clave valida es `text.secondary`, que si resuelve al token y
              responde al cambio de dark/light.
            */}
            <Typography variant='body2' color='text.secondary' style={{ marginBottom: '20px' }}>
              Sign in with the same account you use for your portal to finish setting up how you get
              paid.
            </Typography>
            <AuthPage />
          </Paper>
        </Container>
      </div>
    );
  }

  // ------------------------------------------------------------------ cargando
  // Si acaba de enviar, no tapar la vista de "en revisión" con el skeleton.
  if (loading && !saved) {
    return (
      <div>
        <Header />
        <Container className='container' style={{ paddingTop: '40px', maxWidth: 720 }}>
          <Skeleton variant='text' height={48} width='60%' />
          <Skeleton variant='rectangular' height={220} />
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  // -------------------------------------------------------------------- error
  if (loadError) {
    return (
      <div>
        <Header />
        <Container className='container' style={{ paddingTop: '40px', maxWidth: 720 }}>
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={loadRequest}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        </Container>
      </div>
    );
  }

  // -------------------------------------------------------------- ya enrolado
  if (request?.paymentReady) {
    const readyProviderLabel =
      request.usioEnrolled && request.dwollaEnrolled
        ? 'Usio and Dwolla are both active.'
        : `${request.usioEnrolled ? 'Usio' : 'Dwolla'} is active.`;

    return (
      <PaymentSetupStatusView
        icon={<CheckCircle color='success' style={{ fontSize: 56 }} aria-hidden='true' />}
        title='Your payment account is ready'
        description={`${readyProviderLabel} You can collect payments from your portal.`}
        portalUrl={portalUrl}
      />
    );
  }

  // ------------------------------------------------------- enviada, en revision
  if (isAwaitingReview(request, saved)) {
    const submittedProvider = providerLabel(request?.providerChoice || null);

    return (
      <PaymentSetupStatusView
        icon={<HourglassEmpty color='primary' style={{ fontSize: 56 }} aria-hidden='true' />}
        title='Your application is under review'
        description={`We received your ${submittedProvider} information. Our team is reviewing it now and we will email you when your account is approved. You cannot collect payments until then.`}
        portalUrl={portalUrl}
      />
    );
  }

  // ------------------------------------------------------------------- formulario
  return (
    <div>
      <Header />
      <Container className='container' style={{ paddingTop: '40px', maxWidth: 960 }}>
        <Typography variant='h5' gutterBottom style={{ fontWeight: 'bold' }}>
          Set up how you get paid
        </Typography>
        <Typography variant='body1' color='text.secondary' style={{ marginBottom: '24px' }}>
          Your portal is ready, but you cannot collect payments until you finish this step. Use the
          same application you would fill during first-time enrollment.
        </Typography>
      </Container>
      <EnrollmentSteps source='payment-setup' onSubmitted={handleEnrollmentSubmitted} />
    </div>
  );
};

export default PaymentSetupPage;
